<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import MessageApi from 'dashboard/api/inbox/message';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import WorkflowChart from 'shared/components/WorkflowChart.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    required: false,
    default: null,
  },
});

const emit = defineEmits(['close']);

const route = useRoute();
const { t } = useI18n();
const currentStep = ref('merge'); // 'merge' or 'workflow'
const messages = ref([]);
const isLoading = ref(false);
const error = ref(null);

// Workflow related state
const workflowData = ref(null);
const isLoadingWorkflow = ref(false);
const workflowError = ref(null);
const currentViewMode = ref('structured'); // 'text-view', 'structured', 'simple'

// Get conversation ID from prop or route as fallback
const conversationId = computed(() => {
  if (props.conversationId) {
    return parseInt(props.conversationId, 10);
  }
  return parseInt(route.params.conversationId, 10) || null;
});

// Separate messages by type
const humanMessages = computed(() => {
  return messages.value.filter(
    message => message.message_type === MESSAGE_TYPE.INCOMING
  );
});

const agentMessages = computed(() => {
  return messages.value.filter(
    message => message.message_type === MESSAGE_TYPE.OUTGOING
  );
});

// Parse workflow steps from text diagram
const parseWorkflowSteps = textDiagram => {
  if (!textDiagram) return [];

  const lines = textDiagram.split('\n');

  return lines
    .map(line => line.trim())
    .filter(line => line.startsWith('User:') || line.startsWith('Agent:'))
    .map(line => {
      if (line.startsWith('User:')) {
        return {
          type: 'user',
          message: line.replace('User:', '').trim().replace(/["""]/g, ''),
        };
      }
      return {
        type: 'agent',
        message: line.replace('Agent:', '').trim().replace(/["""]/g, ''),
      };
    });
};

const fetchMessages = async () => {
  if (!conversationId.value) {
    return;
  }

  try {
    isLoading.value = true;
    error.value = null;
    const response = await MessageApi.getPreviousMessages({
      conversationId: conversationId.value,
      before: null, // Get all messages
    });
    messages.value = response.data.payload || [];
    // Scroll to bottom after messages are loaded
    await nextTick();
    const agentContainer = document.querySelector('.agent-messages-container');
    const humanContainer = document.querySelector('.human-messages-container');

    if (agentContainer) {
      agentContainer.scrollTop = agentContainer.scrollHeight;
    }

    if (humanContainer) {
      humanContainer.scrollTop = humanContainer.scrollHeight;
    }
  } catch (err) {
    error.value = t(
      'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.FAILED_TO_FETCH'
    );
    // eslint-disable-next-line no-console
    console.error('Error fetching messages:', err);
  } finally {
    isLoading.value = false;
  }
};

const generateWorkflow = async () => {
  if (!conversationId.value) {
    return;
  }

  try {
    isLoadingWorkflow.value = true;
    workflowError.value = null;

    const response = await ConversationApi.generateWorkflow(
      conversationId.value
    );

    workflowData.value = response.data;
    currentStep.value = 'workflow';
  } catch (err) {
    workflowError.value = t(
      'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.FAILED_TO_GENERATE_WORKFLOW'
    );
    // eslint-disable-next-line no-console
    console.error('Error generating workflow:', err);
  } finally {
    isLoadingWorkflow.value = false;
  }
};

const closeModal = () => {
  emit('close');
  currentStep.value = 'merge'; // Reset to first step when closing
  workflowData.value = null; // Reset workflow data
  workflowError.value = null;
  currentViewMode.value = 'structured'; // Reset view mode
};

const formatTimestamp = timestamp => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const exportWorkflowAsPNG = async () => {
  if (!workflowData.value) return;

  try {
    // Create a canvas from the workflow content
    const html2canvas = await import('html2canvas');
    const element = document.querySelector('.workflow-diagram-container');

    if (element) {
      const canvas = await html2canvas.default(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });

      // Create download link
      const link = document.createElement('a');
      link.download = `workflow-${workflowData.value.workflow_id}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error exporting workflow:', err);
  }
};

const setViewMode = mode => {
  currentViewMode.value = mode;
};

// Detect theme for WorkflowChart
const currentTheme = computed(() => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light';
  }
  return 'light';
});

// Watch for modal open/close and conversation changes
watch(
  [() => props.isOpen, conversationId],
  ([isOpen, convId]) => {
    if (isOpen && convId) {
      fetchMessages();
    }
  },
  { immediate: true }
);

// Watch for messages changes to scroll to bottom
watch(
  messages,
  () => {
    nextTick(() => {
      const agentContainer = document.querySelector(
        '.agent-messages-container'
      );
      const humanContainer = document.querySelector(
        '.human-messages-container'
      );

      if (agentContainer) {
        agentContainer.scrollTop = agentContainer.scrollHeight;
      }

      if (humanContainer) {
        humanContainer.scrollTop = humanContainer.scrollHeight;
      }
    });
  },
  { deep: true }
);

onMounted(() => {
  if (props.isOpen && conversationId.value) {
    fetchMessages();
  }
});
</script>

<template>
  <div
    v-show="isOpen"
    class="fixed inset-0 bg-modal-backdrop-light dark:bg-modal-backdrop-dark flex items-center justify-center z-[9999] p-4"
    @click.self="closeModal"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col border border-slate-200 dark:border-slate-700"
    >
      <!-- Modal Header -->
      <div class="flex items-center justify-between px-4 pt-4">
        <div class="flex items-center space-x-2">
          <span class="text-slate-600 dark:text-slate-400">{{
            t('AGENTIC_ASSISTANCE.RLHFL.TITLE')
          }}</span>
          <template v-if="currentStep === 'workflow'">
            <span class="text-slate-400 dark:text-slate-500">&gt;</span>
            <span class="text-slate-900 dark:text-slate-100 font-medium">{{
              t('AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.WORKFLOWS')
            }}</span>
          </template>
        </div>
        <div class="flex items-center space-x-4">
          <div class="flex items-center space-x-2">
            <button class="w-6 h-6 flex items-center justify-center">
              <svg
                class="w-4 h-4 text-slate-600 dark:text-slate-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                />
              </svg>
            </button>
          </div>
          <button
            class="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            @click="closeModal"
          >
            <svg
              class="w-4 h-4 text-slate-600 dark:text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Modal Content -->
      <div class="flex-1 overflow-hidden">
        <!-- Step 1: Conversation Merge View -->
        <template v-if="currentStep === 'merge'">
          <div class="h-full px-6 py-4 overflow-hidden">
            <div class="grid grid-cols-2 gap-6 h-full">
              <!-- Left Box: Agent Conversation -->
              <div
                class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 flex flex-col h-full"
              >
                <!-- Agent Conversation Header -->
                <div
                  class="flex items-center bg-n-weak rounded-t-lg justify-between p-3 border-b border-slate-200 dark:border-slate-700 flex-shrink-0"
                >
                  <div class="flex items-center space-x-2">
                    <h3
                      class="text-xs text-n-text-muted font-medium text-slate-900 dark:text-slate-100"
                    >
                      {{
                        t(
                          'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.AGENT_CONVERSATION'
                        )
                      }}
                    </h3>
                    <span class="text-xs text-slate-500 dark:text-slate-400">{{
                      new Date().toLocaleDateString()
                    }}</span>
                    <span
                      class="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full"
                    >
                      &#43;{{ agentMessages.length }}
                    </span>
                  </div>
                </div>

                <!-- Agent Conversation Messages -->
                <div
                  class="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 agent-messages-container"
                >
                  <!-- Loading State -->
                  <template v-if="isLoading">
                    <div class="flex justify-center items-center p-4">
                      <div
                        class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500"
                      />
                    </div>
                  </template>

                  <!-- Error State -->
                  <template v-else-if="error">
                    <div class="p-4 text-center text-red-500">
                      {{ error }}
                    </div>
                  </template>

                  <!-- No Messages -->
                  <template v-else-if="agentMessages.length === 0">
                    <div
                      class="p-4 text-center text-slate-500 dark:text-slate-400"
                    >
                      {{
                        t(
                          'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.NO_AGENT_MESSAGES'
                        )
                      }}
                    </div>
                  </template>

                  <!-- Agent Messages -->
                  <template v-else>
                    <div
                      v-for="message in agentMessages"
                      :key="message.id"
                      class="flex flex-col items-end mb-4"
                    >
                      <div
                        class="bg-gradient-to-r rounded-2xl rounded-br-sm from-[#E9F2FF] dark:from-[#242632] to-[#D5DFEC] dark:to-[#292542] text-[#2226DC] dark:text-[#FFFFFF] px-4 py-2 w-auto max-w-[300px]"
                      >
                        <div class="rounded-lg">
                          <p class="text-xs" v-html="message.content" />
                        </div>
                      </div>
                      <p
                        class="text-xs mt-1 text-slate-500 dark:text-slate-400"
                      >
                        {{
                          message.sender?.available_name ||
                          message.sender?.name ||
                          t(
                            'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.AGENT_FALLBACK'
                          )
                        }}
                        {{ formatTimestamp(message.created_at) }}
                      </p>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Right Box: Human Conversation -->
              <div
                class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 flex flex-col h-full"
              >
                <!-- Human Conversation Header -->
                <div
                  class="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700 bg-n-weak rounded-t-lg flex-shrink-0"
                >
                  <div class="flex items-center space-x-2">
                    <h3
                      class="text-xs text-n-text-muted font-medium text-slate-900 dark:text-slate-100"
                    >
                      {{
                        t(
                          'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.HUMAN_CONVERSATION'
                        )
                      }}
                    </h3>
                    <span class="text-xs text-slate-500 dark:text-slate-400">{{
                      new Date().toLocaleDateString()
                    }}</span>
                    <span
                      class="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full"
                    >
                      &#43;{{ humanMessages.length }}
                    </span>
                  </div>
                </div>

                <!-- Human Conversation Messages -->
                <div
                  class="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 human-messages-container"
                >
                  <!-- Loading State -->
                  <template v-if="isLoading">
                    <div class="flex justify-center items-center p-4">
                      <div
                        class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500"
                      />
                    </div>
                  </template>

                  <!-- Error State -->
                  <template v-else-if="error">
                    <div class="p-4 text-center text-red-500">
                      {{ error }}
                    </div>
                  </template>

                  <!-- No Messages -->
                  <template v-else-if="humanMessages.length === 0">
                    <div
                      class="p-4 text-center text-slate-500 dark:text-slate-400"
                    >
                      {{
                        t(
                          'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.NO_HUMAN_MESSAGES'
                        )
                      }}
                    </div>
                  </template>

                  <!-- Human Messages -->
                  <template v-else>
                    <div
                      v-for="message in humanMessages"
                      :key="message.id"
                      class="flex flex-col items-end mb-4"
                    >
                      <div
                        class="bg-gradient-to-r rounded-2xl rounded-br-sm from-[#E9F2FF] dark:from-[#242632] to-[#D5DFEC] dark:to-[#292542] text-[#2226DC] dark:text-[#FFFFFF] px-4 py-2 w-auto max-w-[300px]"
                      >
                        <div class="rounded-lg">
                          <p class="text-xs" v-html="message.content" />
                        </div>
                      </div>
                      <p
                        class="text-xs mt-1 text-slate-500 dark:text-slate-400"
                      >
                        {{
                          message.sender?.available_name ||
                          message.sender?.name ||
                          t(
                            'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.CUSTOMER_FALLBACK'
                          )
                        }}
                        {{ formatTimestamp(message.created_at) }}
                      </p>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Step 2: Workflow Diagram View -->
        <template v-if="currentStep === 'workflow'">
          <div class="h-full p-6 overflow-auto dark:bg-slate-800">
            <!-- Loading State -->
            <template v-if="isLoadingWorkflow">
              <div class="flex justify-center items-center h-full">
                <div class="text-center">
                  <div
                    class="animate-spin rounded-full h-12 w-12 border-b-2 border-woot-500 mx-auto mb-4"
                  />
                  <p class="text-slate-600 dark:text-slate-400">
                    {{
                      t(
                        'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.GENERATING_WORKFLOW'
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Error State -->
            <template v-else-if="workflowError">
              <div class="flex justify-center items-center h-full">
                <div class="text-center">
                  <div class="text-red-500 mb-4">
                    <svg
                      class="w-12 h-12 mx-auto"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <p class="text-red-500">{{ workflowError }}</p>
                </div>
              </div>
            </template>

            <!-- Workflow Content -->
            <template v-else-if="workflowData">
              <div class="workflow-diagram-container">
                <!-- Text View Mode -->
                <template v-if="currentViewMode === 'text-view'">
                  <pre
                    class="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300 font-mono bg-slate-50 dark:bg-slate-800 p-4 rounded border overflow-auto"
                    >{{ workflowData.text_diagram }}</pre
                  >
                </template>

                <!-- Structured View Mode -->
                <template v-else-if="currentViewMode === 'structured'">
                  <div
                    class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
                  >
                    <div class="h-[450px] p-0">
                      <WorkflowChart
                        :workflow-data="workflowData"
                        :theme="currentTheme"
                      />
                    </div>
                  </div>
                </template>

                <!-- Simple View Mode -->
                <template v-else-if="currentViewMode === 'simple'">
                  <div
                    class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-6"
                  >
                    <h2
                      class="text-lg font-semibold mb-6 text-slate-900 dark:text-slate-100 text-center"
                    >
                      {{
                        t(
                          'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.WORKFLOW.SIMPLE_DIAGRAM'
                        )
                      }}
                    </h2>

                    <!-- Simple Flow Visualization -->
                    <div class="space-y-4 max-w-2xl mx-auto">
                      <!-- Start Node -->
                      <div class="flex justify-center">
                        <div
                          class="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-6 py-3 rounded-full text-sm font-medium border-2 border-green-200 dark:border-green-700"
                        >
                          {{
                            t(
                              'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.WORKFLOW.START'
                            )
                          }}
                        </div>
                      </div>

                      <!-- Arrow Down -->
                      <div class="flex justify-center">
                        <div class="text-blue-500 text-2xl">
                          {{
                            t(
                              'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.ARROW_DOWN'
                            )
                          }}
                        </div>
                      </div>

                      <!-- Workflow Steps from text_diagram -->
                      <div class="space-y-3 w-full">
                        <template
                          v-for="(step, index) in parseWorkflowSteps(
                            workflowData.text_diagram
                          )"
                          :key="index"
                        >
                          <div class="flex justify-center">
                            <div
                              class="px-4 py-3 rounded-lg text-sm max-w-md text-center border-2 shadow-sm"
                              :class="[
                                step.type === 'user'
                                  ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-700'
                                  : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700',
                              ]"
                            >
                              <strong class="block mb-1"
                                >{{
                                  step.type === 'user'
                                    ? t(
                                        'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.USER'
                                      )
                                    : t(
                                        'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.AGENT'
                                      )
                                }}{{
                                  t(
                                    'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.COLON'
                                  )
                                }}</strong
                              >
                              <span class="text-xs">{{ step.message }}</span>
                            </div>
                          </div>
                          <div
                            v-if="
                              index <
                              parseWorkflowSteps(workflowData.text_diagram)
                                .length -
                                1
                            "
                            class="flex justify-center"
                          >
                            <div class="text-blue-500 text-xl">
                              {{
                                t(
                                  'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.ARROW_DOWN'
                                )
                              }}
                            </div>
                          </div>
                        </template>
                      </div>

                      <!-- Arrow Down -->
                      <div class="flex justify-center">
                        <div class="text-blue-500 text-2xl">
                          {{
                            t(
                              'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.ARROW_DOWN'
                            )
                          }}
                        </div>
                      </div>

                      <!-- End Node -->
                      <div class="flex justify-center">
                        <div
                          class="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-6 py-3 rounded-full text-sm font-medium border-2 border-red-200 dark:border-red-700"
                        >
                          {{
                            t(
                              'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.WORKFLOW.END'
                            )
                          }}
                        </div>
                      </div>
                    </div>

                    <!-- Mini-map placeholder -->
                    <div
                      class="absolute bottom-4 right-4 bg-slate-100 dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-700"
                    >
                      <div
                        class="text-xs text-slate-600 dark:text-slate-400 mb-1"
                      >
                        {{
                          t(
                            'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.WORKFLOW.REACT_FLOW'
                          )
                        }}
                      </div>
                      <div
                        class="w-24 h-16 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center"
                      >
                        <div class="text-xs text-slate-500 dark:text-slate-400">
                          {{
                            t(
                              'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.WORKFLOW.MINI_MAP'
                            )
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </template>
      </div>

      <!-- Modal Footer -->
      <div class="flex items-center justify-between px-6 pb-6">
        <div class="flex space-x-2">
          <button
            v-if="currentStep === 'workflow'"
            class="px-4 py-2 text-sm font-medium border rounded transition-colors"
            :class="[
              currentViewMode === 'text-view'
                ? 'bg-woot-500 text-white border-woot-500'
                : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700',
            ]"
            @click="setViewMode('text-view')"
          >
            {{
              t('AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.TEXT_VIEW')
            }}
          </button>
          <button
            v-if="currentStep === 'workflow'"
            class="px-4 py-2 text-sm font-medium border rounded transition-colors"
            :class="[
              currentViewMode === 'structured'
                ? 'bg-woot-500 text-white border-woot-500'
                : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700',
            ]"
            @click="setViewMode('structured')"
          >
            {{
              t(
                'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.STRUCTURED'
              )
            }}
          </button>
          <button
            v-if="currentStep === 'workflow'"
            class="px-4 py-2 text-sm font-medium border rounded transition-colors"
            :class="[
              currentViewMode === 'simple'
                ? 'bg-woot-500 text-white border-woot-500'
                : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700',
            ]"
            @click="setViewMode('simple')"
          >
            {{
              t('AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.SIMPLE')
            }}
          </button>
        </div>

        <div class="flex space-x-3">
          <button
            v-if="currentStep === 'workflow'"
            class="px-4 py-2 text-sm font-medium text-slate-700 bg-n-weak dark:text-slate-300 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            @click="currentStep = 'merge'"
          >
            {{
              t(
                'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.BACK_TO_CONVERSATIONS'
              )
            }}
          </button>
          <button
            v-if="currentStep === 'workflow'"
            class="px-4 py-2 text-sm font-medium text-white bg-woot-500 dark:text-white dark:bg-woot-500 rounded hover:bg-woot-700 dark:hover:bg-woot-700 transition-colors"
            @click="exportWorkflowAsPNG"
          >
            {{
              t(
                'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.EXPORT_PNG'
              )
            }}
          </button>
          <button
            v-if="currentStep === 'merge'"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-n-weak dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            @click="closeModal"
          >
            {{
              t('AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.CANCEL')
            }}
          </button>
          <button
            v-if="currentStep === 'merge'"
            class="px-4 py-2 text-sm font-medium bg-woot-500 text-white dark:text-white dark:bg-woot-500 rounded hover:bg-woot-700 dark:hover:bg-woot-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isLoadingWorkflow"
            @click="generateWorkflow"
          >
            <div
              v-if="isLoadingWorkflow"
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
            />
            {{
              isLoadingWorkflow
                ? t(
                    'AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.GENERATING'
                  )
                : t('AGENTIC_ASSISTANCE.CONVERSATION_MERGE_MODAL.BUTTONS.MERGE')
            }}
            <svg
              v-if="!isLoadingWorkflow"
              class="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar styling for conversation boxes */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}

/* Ensure proper flex layout */
.flex-1 {
  min-height: 0;
}

/* Ensure scroll containers have proper height */
.agent-messages-container,
.human-messages-container {
  max-height: calc(100vh - 300px);
  min-height: 200px;
}
</style>
